import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FeedSort } from '../model/contracts'

const SORT_OPTIONS: FeedSort[] = ['newest', 'oldest', 'engagement']

type FeedSortDropdownProps = {
  value: FeedSort
  onChange: (next: FeedSort) => void
}

export const FeedSortDropdown = memo(function FeedSortDropdown({
  value,
  onChange,
}: FeedSortDropdownProps) {
  const { t } = useTranslation('feed')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-medium transition-colors',
          'border-border bg-muted/40 text-muted-foreground hover:bg-muted/60',
        )}
      >
        <span>{t(`sort.${value}`)}</span>
        <ChevronDown
          size={12}
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-lg border border-border bg-card shadow-md">
          <div className="border-b border-border px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('sort.label')}
            </span>
          </div>

          {SORT_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option)
                setOpen(false)
              }}
              className={cn(
                'flex w-full items-center px-3 py-2 text-[13px] transition-colors hover:bg-muted/40',
                value === option ? 'font-medium text-primary' : 'text-foreground',
              )}
            >
              {t(`sort.${option}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
})
